import { Field, Form, Formik } from 'formik';
import pluralize from 'pluralize';
import React, { useRef } from 'react';

import { PrimaryButton, ProButton, TextButton } from '@components/Button/variants';
import SearchIcon from '@components/Images/SearchIcon';
import useEventTracking from '@hooks/useEventTracking';
import useWindowSize from '@hooks/useWindowSize';
import { deep_research_path, search_page_path } from '@routes';
import { ClickTarget, click } from '@tracking';
import { Scope, SortOption } from '@utils/backend-api/search';
import { WIDTH_SM } from '@utils/constants';
import { cleanQueryForClickText } from '@utils/tracking';

const DEEP_RESEARCH_SEARCH_BUTTON_LABEL = 'Deep Research Search Submit';
const REGULAR_SEARCH_BUTTON_LABEL = 'Search Submit';

interface SearchBarProps {
  isDeepResearchActive?: boolean;
  onClearSearch: () => void;
  onSearchSubmit: (query: string) => void;
  scope: Scope;
  searchQuery?: string;
  sort?: SortOption;
  totalResults?: number;
}

export default function SearchBar({
  isDeepResearchActive = false,
  onClearSearch,
  onSearchSubmit,
  scope,
  searchQuery,
  sort,
  totalResults,
}: SearchBarProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const SearchButton = isDeepResearchActive ? ProButton : PrimaryButton;
  const buttonLabel = isDeepResearchActive
    ? DEEP_RESEARCH_SEARCH_BUTTON_LABEL
    : REGULAR_SEARCH_BUTTON_LABEL;

  const { width: windowWidth } = useWindowSize();

  const { trackEvent } = useEventTracking({});

  const handleSubmit = (values: { query: string; scope: Scope }) => {
    // Build the URL with query parameters
    const basePath = isDeepResearchActive ? deep_research_path() : search_page_path();
    const cleanedQuery = cleanQueryForClickText(values.query);
    const searchParams = new URLSearchParams({
      query: cleanedQuery,
    });

    if (!isDeepResearchActive) {
      searchParams.set('scope', values.scope.toString());
      if (sort) searchParams.set('sort', sort);
    }

    const newLink = `${basePath}?${searchParams.toString()}`;

    // Track the search event
    trackEvent(
      click({
        clickTarget: isDeepResearchActive
          ? ClickTarget.DeepResearchSubmit
          : ClickTarget.RegularSearchSubmit,
        clickText: cleanedQuery,
        clickLink: newLink,
      })
    );

    if (isDeepResearchActive) {
      window.location.href = newLink;
    } else {
      onSearchSubmit(cleanedQuery);
    }
  };

  const handleClearSearch = () => {
    trackEvent(
      click({
        clickTarget: ClickTarget.ClearSearch,
      })
    );

    onClearSearch();
    inputRef.current?.focus();
  };

  return (
    <div>
      <Formik
        initialValues={{
          query: searchQuery || '',
          scope: scope.toString(),
        }}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form ref={formRef} id="form_id" data-id="search-form">
            <div className="flex w-full flex-col gap-12 sm:flex-row sm:items-center sm:justify-between sm:gap-56">
              <div className="relative w-full">
                <Field
                  innerRef={inputRef}
                  type="text"
                  name="query"
                  className="h-56 w-full rounded-none border-0 border-b border-grayscale-charcoal py-2 pl-0 pr-80 font-suisse-screen text-headline-sm text-primary-black placeholder:text-grayscale-gray/50 focus:outline-none"
                  placeholder={isDeepResearchActive ? 'Ask Deep Research' : 'Search'}
                  autoFocus
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newValue = e.target.value;
                    setFieldValue('query', newValue);
                  }}
                />
                {values.query && (
                  <TextButton
                    as="button"
                    type="button"
                    onClick={() => {
                      setFieldValue('query', '');
                      handleClearSearch();
                    }}
                    className="!absolute right-32 top-1/2 !w-fit -translate-y-1/2 p-0 font-suisse-intl-mono text-overline-sm font-normal !text-grayscale-gray !no-underline"
                    aria-label="Clear search"
                  >
                    clear
                  </TextButton>
                )}
                <SearchIcon className="absolute right-2 top-1/2 h-20 w-20 -translate-y-1/2" />
              </div>
              <SearchButton
                as="button"
                aria-label={buttonLabel}
                className="w-full px-36 sm:w-auto sm:self-end"
                type="submit"
                size={windowWidth < WIDTH_SM ? 'small' : 'big'}
              >
                {isDeepResearchActive ? 'Deep Research' : 'Search'}
              </SearchButton>
            </div>
            <Field type="hidden" name="scope" />
          </Form>
        )}
      </Formik>
      {!!searchQuery && (
        <div className="mt-12 font-suisse-intl-mono text-overline-md text-primary-black">
          {totalResults
            ? `Your search for '${searchQuery}' returned ${totalResults} ${pluralize(
                'result',
                totalResults
              )}`
            : `Results for '${searchQuery}'`}
        </div>
      )}
    </div>
  );
}
